import { SettingsSearchProvider } from "../context/SettingsSearchContext";
import DashboardTopbar from "./DashboardTopbar";
import React, { useState, useEffect } from "react";
import { Typography, Box, Tooltip } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getUser, logout, fetchUserById } from "../services/auth";
import InterviewForm from "./InterviewForm";
import InterviewList from "./InterviewList";
import TemplateList from "./TemplateList";
import TemplateForm from "./TemplateForm";
import { OverviewDashboard } from "./OverviewDashboard";
import UserManagement from "./UserManagement";
import Settings from "./Settings";
import ProctoringAnalysis from "./ProctoringAnalysis";
import { Settings as SettingsIcon } from "lucide-react";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useRBAC } from "../context/RBACContext";
import { RESOURCE_DISPLAY_NAMES, ACTION_NAMES } from "../utils/resourceMapping";

import TalentTalkslogo from "../../interviewAI/assets/Talenttalklogo.png";
import FaviconLogo from "../../interviewAI/assets/FavIconcopy.png";
import "./Dashboard.css";

import dashboardNavIcon from "../assets/DashboardAssets/DASHBOARD.svg";
import interviewNavIcon from "../assets/DashboardAssets/Interviews.svg";
import createJobNavIcon from "../assets/DashboardAssets/CREATE JOB.svg";
import viewJobsNavIcon from "../assets/DashboardAssets/sidebar-view-jobs.svg";
// import reportNavIcon from "../assets/DashboardAssets/REPORTS.svg";
import userControlsNavIcon from "../assets/DashboardAssets/sidebar-user-controls.svg";
import logoutNavIcon from "../assets/DashboardAssets/logout.svg";

export const SidebarInline = ({ isCollapsed, setIsCollapsed, profileUser }) => {
  const [localUser, setLocalUser] = useState(getUser());
  const user = profileUser === undefined ? localUser : profileUser;
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasResourcePermissionByName } = useRBAC();
  useEffect(() => { if (profileUser !== undefined) return undefined; let alive = true; fetchUserById().then(value => { if (alive && value) setLocalUser(value); }).catch(() => {}); return () => { alive = false; }; }, [profileUser]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const restrictedSettings = user?.role?.name === "Super Admin" && user?.scope?.name && !["global", "all"].includes(user.scope.name.toLowerCase());
  const items = [
    ["Dashboard", "/dashboard", RESOURCE_DISPLAY_NAMES.OVERVIEW, ACTION_NAMES.GET_DATA, dashboardNavIcon],
    ["View Interview", "/interviews", RESOURCE_DISPLAY_NAMES.VIEW_INTERVIEWS, ACTION_NAMES.GET_DATA, interviewNavIcon],
    ["Schedule Interview", "/interviews/new", RESOURCE_DISPLAY_NAMES.SCHEDULE_INTERVIEW, ACTION_NAMES.ADD_NEW, interviewNavIcon],
    ["Create Job", "/templates/new", RESOURCE_DISPLAY_NAMES.CREATE_JOB_OPENING, ACTION_NAMES.ADD_NEW, createJobNavIcon],
    ["View Jobs", "/templates", RESOURCE_DISPLAY_NAMES.VIEW_JOB_OPENING, ACTION_NAMES.GET_DATA, viewJobsNavIcon],
    // ["Report", "/proctoring-analysis", RESOURCE_DISPLAY_NAMES.PROCTORING_ANALYSIS, ACTION_NAMES.GET_DATA, reportNavIcon],
    ["User Controls", "/user-management", RESOURCE_DISPLAY_NAMES.USER_MANAGEMENT, ACTION_NAMES.GET_DATA, userControlsNavIcon],
    ["Settings", "/settings", RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS, ACTION_NAMES.GET_DATA, null],
  ].filter(([,path,resource,action]) => hasResourcePermissionByName(resource, action) && !(path === "/settings" && restrictedSettings));
  const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.name || "User";
  const role = user?.role?.name || (typeof user?.role === 'string' ? user.role : "");
  const toggle = () => { setIsCollapsed(!isCollapsed); localStorage.setItem('sidebarCollapsed', JSON.stringify(!isCollapsed)); };
  const signOut = async () => { await logout(); navigate('/', { replace: true }); };
  const active = (path) => path === '/dashboard' ? location.pathname === '/dashboard' : path === '/interviews' || path === '/templates' ? location.pathname.startsWith(path) && !location.pathname.startsWith(`${path}/new`) : location.pathname.startsWith(path);
  return <>
    <div className="ttd-mobile-bar"><img src={TalentTalkslogo} alt="TalentTalks" /><button aria-label="Toggle navigation" aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>{mobileOpen ? <span aria-hidden="true">&times;</span> : <span aria-hidden="true">&#9776;</span>}</button></div>
    {mobileOpen && <button className="ttd-sidebar-backdrop" aria-label="Close navigation" onClick={() => setMobileOpen(false)} />}
    <aside className={`ttd-sidebar ${isCollapsed ? 'ttd-sidebar--collapsed' : ''} ${mobileOpen ? 'ttd-sidebar--open' : ''}`} aria-label="Main navigation">
      <div className="ttd-logo-row"><img src={isCollapsed ? FaviconLogo : TalentTalkslogo} alt="TalentTalks" /><button className="ttd-collapse" aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} onClick={toggle}>{isCollapsed ? <ChevronsRight size={14}/> : <ChevronsLeft size={14}/>}</button></div>
      <nav>{items.map(([label,path,,,asset]) => <Tooltip key={path} title={isCollapsed ? label : ''} placement="right"><button className={`ttd-nav-item ${active(path) ? 'is-active' : ''}`} aria-current={active(path) ? 'page' : undefined} onClick={() => navigate(path)}>{asset ? <img src={asset} alt=""/> : <SettingsIcon size={17}/>}<span>{label}</span></button></Tooltip>)}</nav>
      <div className="ttd-sidebar-footer"><div className="ttd-user"><span className="ttd-avatar">{name[0]?.toUpperCase()}</span><div className="ttd-user-label"><strong>{name}</strong><small>{role}</small></div></div><button className="ttd-nav-item ttd-logout" onClick={signOut}><img src={logoutNavIcon} alt=""/><span>Logout</span></button></div>
    </aside>
  </>;
};

// Empty state component for access denied
const AccessDeniedState = ({ resourceName }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "70vh",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Typography variant="h5" sx={{ fontWeight: 600, color: "#1976d2" }}>
      Access Denied
    </Typography>
    <Typography variant="body1" color="text.secondary">
      You don't have access to {resourceName}. Contact your administrator.
    </Typography>
  </Box>
);

const Dashboard = () => {
  const [activeView, setActiveView] = useState("overview");
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  const { hasResourcePermissionByName } = useRBAC();
  const [user, setUser] = useState(getUser());
  
  // Load user data
  useEffect(() => {
    const loadUser = async () => {
      const apiUser = await fetchUserById();
      if (apiUser) {
        setUser(apiUser);
      } else {
        setUser(getUser());
      }
    };
    loadUser();
  }, []);

  // Check permissions for each resource
  const canAccessOverview = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.OVERVIEW,
    ACTION_NAMES.GET_DATA,
  );
  const canCreateTemplate = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.CREATE_JOB_OPENING,
    ACTION_NAMES.ADD_NEW,
  );
  const canViewTemplates = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.VIEW_JOB_OPENING,
    ACTION_NAMES.GET_DATA,
  );
  const canScheduleInterview = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SCHEDULE_INTERVIEW,
    ACTION_NAMES.ADD_NEW,
  );
  const canViewInterviews = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.VIEW_INTERVIEWS,
    ACTION_NAMES.GET_DATA,
  );
  const canAccessProctoringAnalysis = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.PROCTORING_ANALYSIS,
    ACTION_NAMES.GET_DATA,
  );
  const canManageUsers = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.USER_MANAGEMENT,
    ACTION_NAMES.GET_DATA,
  );
  const canAccessSettings = hasResourcePermissionByName(
    RESOURCE_DISPLAY_NAMES.SYSTEM_SETTINGS,
    ACTION_NAMES.GET_DATA,
  );
  
  // Check if Super Admin with NON-global/non-all scope (hide User Management & Settings)
        const isSuperAdminWithoutGlobalScope = 
          user?.role?.name === "Super Admin" && 
          user?.scope?.name && // Only hide if scope exists
          !["global", "all"].includes(user?.scope?.name?.toLowerCase());

  const renderContent = () => {
    switch (activeView) {
      case "overview":
        return canAccessOverview ? (
          <OverviewDashboard profileUser={user} />
        ) : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="the Overview dashboard" />
          </Box>
        );
      case "schedule":
        return canScheduleInterview ? (
          <InterviewForm />
        ) : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="interview scheduling" />
          </Box>
        );
      case "interviews":
        return canViewInterviews ? (
          <InterviewList />
        ) : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="the interviews section" />
          </Box>
        );
      case "templates":
        return canViewTemplates ? (
          <TemplateList />
        ) : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="job opening templates" />
          </Box>
        );
      case "createTemplate":
        return canCreateTemplate ? (
          <TemplateForm
            onSuccess={() => setActiveView("templates")}
            onCancel={() => setActiveView("templates")}
          />
        ) : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="template creation" />
          </Box>
        );
      case "userManagement":
        return canManageUsers ? (
          <UserManagement />
        ) : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="user management" />
          </Box>
        );
      case "proctoringAnalysis":
        return canAccessProctoringAnalysis ? (
          <Box sx={{ width: "100%", px: { xs: 2, md: 6 }, py: 4 }}>
            <ProctoringAnalysis />
          </Box>
        ) : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="proctoring analysis" />
          </Box>
        );
      case "settings":
        return canAccessSettings && !isSuperAdminWithoutGlobalScope ? <Settings /> : (
          <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
            <AccessDeniedState resourceName="settings" />
          </Box>
        );
      default:
        return null;
    }
  };

  const sidebarWidth = isCollapsed ? "76px" : "236px";

  return (
    <SettingsSearchProvider>
    <div className="ttd-shell" style={{ display: "flex", minHeight: "100vh" }}>
      <SidebarInline
        profileUser={user}
        activeView={activeView}
        setActiveView={setActiveView}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
      />
      <Box
        sx={{
          flex: 1,
          background: "#f8f6ff",
          pl: { xs: 0, md: sidebarWidth },
          mt: { xs: "4rem", md: 0 },
          overflowY: "auto",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <DashboardTopbar profileUser={user} />
        {renderContent()}
      </Box>
    </div>
    </SettingsSearchProvider>
  );
};

export default Dashboard;
